/**
 * The Tasks model picker (model picker plan, step c).
 *
 * Every option is visible rather than hidden in a dropdown:
 *
 * - **Featured**: the curated short list Lance approved (``featuredModels.ts``,
 *   decision D3).
 * - **Tested by Auteur**: models with a shipped dossier (decision D2), each
 *   with plain strengths and weaknesses in the details panel.
 * - **Available models**: when neither group has anything for this key's
 *   provider, the cheapest few, so there is always a visible choice.
 * - **Browse all**: the whole priced catalogue, searchable and filterable,
 *   where every model has a details view, not just the recommended ones.
 *
 * "Recommended for you" arrives with intake and preferences (steps d and e).
 * The grouping and filtering rules are plain functions so they can be tested
 * without a browser.
 */

import React, { useMemo, useState } from 'react';
import { Link } from 'wouter';

import { TaskModelOption } from '../../api/tasks';
import { HelpTip } from '../../components/HelpTip';
import { DEFAULT_MODELS, FEATURED_MODELS, FeaturedModel } from './featuredModels';

export const PROVIDER_NAMES: Record<string, string> = { openai: 'OpenAI', anthropic: 'Anthropic', openrouter: 'OpenRouter' };
/** How many models "Available models" shows when nothing is featured or tested. */
export const FALLBACK_COUNT = 8;
/** Rows the catalogue renders at once; a search narrows the rest. */
export const CATALOG_ROW_LIMIT = 100;
/** Options a group shows before "Show N more" (the chosen model always shows). */
export const GROUP_VISIBLE = 6;

/** The options a group shows while collapsed: the first few, plus the chosen one. */
export function visibleOptions(options: PickerOption[], selected: string, expanded: boolean): PickerOption[] {
  if (expanded || options.length <= GROUP_VISIBLE) return options;
  const head = options.slice(0, GROUP_VISIBLE);
  const chosen = options.find((item) => modelId(item.option) === selected);
  return chosen && !head.includes(chosen) ? [...head, chosen] : head;
}

export function modelId(option: Pick<TaskModelOption, 'provider' | 'model'>): string {
  return `${option.provider}/${option.model}`;
}

export function displayName(option: TaskModelOption): string {
  return option.name || option.model;
}

export function perMtok(value: number): string {
  return `$${value < 1 ? value.toFixed(2) : value.toFixed(2).replace(/\.00$/, '')}`;
}

export function contextLabel(tokens: number | undefined): string {
  if (!tokens) return '';
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(tokens % 1_000_000 === 0 ? 0 : 2).replace(/\.?0+$/, '')}M`;
  return `${Math.round(tokens / 1000)}k`;
}

export interface PickerOption {
  option: TaskModelOption;
  /** One line under the name: featured copy, the top strength, or the catalogue description. */
  why: string;
  featured?: FeaturedModel;
}

export interface PickerGroup {
  id: 'current' | 'featured' | 'tested' | 'available';
  title: string;
  options: PickerOption[];
}

function whyLine(option: TaskModelOption, featured?: FeaturedModel): string {
  if (featured) return featured.bestFor;
  const strength = option.dossier?.strengths[0]?.text;
  if (strength) return strength;
  const description = option.description ?? '';
  const sentence = description.split(/(?<=\.)\s/)[0] ?? '';
  return sentence.length > 140 ? `${sentence.slice(0, 137)}…` : sentence;
}

/**
 * The visible groups for this key's models. ``selected`` (a model id within
 * ``models``) is always visible: it gets its own "Your choice" group when no
 * other group holds it.
 */
export function pickerGroups(
  models: TaskModelOption[],
  selected = '',
  featured: ReadonlyArray<FeaturedModel> = FEATURED_MODELS,
): PickerGroup[] {
  const byId = new Map(models.map((option) => [modelId(option), option]));
  const groups: PickerGroup[] = [];
  const shown = new Set<string>();

  const featuredOptions = featured
    .map((entry) => ({ entry, option: byId.get(modelId(entry)) }))
    .filter((pair): pair is { entry: FeaturedModel; option: TaskModelOption } => !!pair.option)
    .map(({ entry, option }) => ({ option, featured: entry, why: whyLine(option, entry) }));
  if (featuredOptions.length) {
    groups.push({ id: 'featured', title: 'Featured', options: featuredOptions });
    featuredOptions.forEach((item) => shown.add(modelId(item.option)));
  }

  const tested = models
    .filter((option) => option.dossier && !shown.has(modelId(option)))
    .sort((a, b) => a.output_per_mtok - b.output_per_mtok || displayName(a).localeCompare(displayName(b)))
    .map((option) => ({ option, why: whyLine(option) }));
  if (tested.length) {
    groups.push({ id: 'tested', title: 'Tested by Auteur', options: tested });
    tested.forEach((item) => shown.add(modelId(item.option)));
  }

  if (groups.length === 0 && models.length) {
    const cheapest = [...models]
      .sort((a, b) => a.output_per_mtok - b.output_per_mtok || displayName(a).localeCompare(displayName(b)))
      .slice(0, FALLBACK_COUNT)
      .map((option) => ({ option, why: whyLine(option) }));
    groups.push({ id: 'available', title: 'Available models', options: cheapest });
    cheapest.forEach((item) => shown.add(modelId(item.option)));
  }

  const current = selected ? byId.get(selected) : undefined;
  if (current && !shown.has(selected)) {
    groups.unshift({ id: 'current', title: 'Chosen from all models', options: [{ option: current, why: whyLine(current) }] });
  }
  return groups;
}

/**
 * The model a new task starts on: the approved default (GPT-6 Luna) when this
 * key's models include it, otherwise the first visible option.
 */
export function defaultModel(
  models: TaskModelOption[],
  featured: ReadonlyArray<FeaturedModel> = FEATURED_MODELS,
  defaults: ReadonlyArray<{ provider: string; model: string }> = DEFAULT_MODELS,
): TaskModelOption | null {
  const candidates = models.filter((option) => !option.opt_in_only);
  for (const preferred of defaults) {
    const found = candidates.find((option) => modelId(option) === modelId(preferred));
    if (found) return found;
  }
  return pickerGroups(candidates, '', featured)[0]?.options[0]?.option ?? null;
}

export interface CatalogFilters {
  tested?: boolean;
  cheap?: boolean;
  long?: boolean;
  images?: boolean;
}

export const CATALOG_FILTERS: ReadonlyArray<{ key: keyof CatalogFilters; label: string }> = [
  { key: 'tested', label: 'Tested by Auteur' },
  { key: 'cheap', label: 'Under $1 / M out' },
  { key: 'long', label: '1M+ context' },
  { key: 'images', label: 'Takes images' },
];

/** Catalogue search: every word must appear in the id, name or description. */
export function filterCatalog(models: TaskModelOption[], query: string, filters: CatalogFilters = {}): TaskModelOption[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  return models
    .filter((option) => {
      const haystack = `${option.provider} ${option.model} ${option.name ?? ''} ${option.description ?? ''}`.toLowerCase();
      if (!words.every((word) => haystack.includes(word))) return false;
      if (filters.tested && !option.dossier) return false;
      if (filters.cheap && !(option.output_per_mtok < 1)) return false;
      if (filters.long && !((option.context_length ?? 0) >= 1_000_000)) return false;
      if (filters.images && !(option.input_modalities ?? []).includes('image')) return false;
      return true;
    })
    .sort((a, b) => Number(!!b.dossier) - Number(!!a.dossier) || displayName(a).localeCompare(displayName(b)));
}

function accepts(option: TaskModelOption): string {
  const modalities = option.input_modalities ?? [];
  return modalities.length ? modalities.join(', ') : '';
}

/** Everything known about one model: catalogue facts, our dossier, and the caveats. */
export function ModelDetails({ option, dossierNote, featured }: {
  option: TaskModelOption;
  dossierNote?: string;
  featured?: FeaturedModel;
}): React.ReactElement {
  const dossier = option.dossier;
  const abilities = [option.supports_tools ? 'tools' : '', option.supports_reasoning ? 'reasoning' : ''].filter(Boolean);
  return (
    <div className="model-details">
      <div className="model-details-title">
        <strong>{displayName(option)}</strong>
        {dossier && <span className="badge badge-dossier">Tested by Auteur</span>}
      </div>
      <p className="model-details-id">{PROVIDER_NAMES[option.provider] ?? option.provider} · {option.model}</p>
      {featured && (
        <p className="model-details-featured">
          <span>{featured.bestFor}</span> <span className="model-details-caution">{featured.caution}</span>
        </p>
      )}
      <dl>
        <dt>Price</dt>
        <dd>{perMtok(option.input_per_mtok)} in · {perMtok(option.output_per_mtok)} out, per million tokens</dd>
        {option.context_length ? (<><dt>Context</dt><dd>{contextLabel(option.context_length)} tokens</dd></>) : null}
        {accepts(option) ? (<><dt>Accepts</dt><dd>{accepts(option)}</dd></>) : null}
        {abilities.length ? (<><dt>Supports</dt><dd>{abilities.join(', ')}</dd></>) : null}
      </dl>
      {option.tiered_pricing && (
        <p className="field-hint">
          This model charges more for very long prompts. The prices shown are those higher rates, so the
          spend limit is never exceeded; ordinary runs cost less.
        </p>
      )}
      {option.description && <p className="model-details-description">{option.description}</p>}
      {dossier ? (
        <div className="model-dossier">
          {dossier.tested_for.length > 0 && <p>Tested for {dossier.tested_for.join(', ')}, based on {dossier.runs} runs.</p>}
          {dossier.strengths.length > 0 && (
            <ul className="dossier-list dossier-strengths" aria-label="Strengths">
              {dossier.strengths.map((item) => <li key={item.text}>{item.text} <span className="dossier-runs">({item.runs} runs)</span></li>)}
            </ul>
          )}
          {dossier.weaknesses.length > 0 && (
            <ul className="dossier-list dossier-weaknesses" aria-label="Weaknesses">
              {dossier.weaknesses.map((item) => <li key={item.text}>{item.text} <span className="dossier-runs">({item.runs} runs)</span></li>)}
            </ul>
          )}
          {dossier.strengths.length === 0 && dossier.weaknesses.length === 0 && (
            <p>No clear strengths or weaknesses yet.</p>
          )}
          <p className="field-hint">
            {dossier.last_tested ? `Last tested ${dossier.last_tested}. ` : ''}{dossierNote ?? ''}
          </p>
        </div>
      ) : (
        <p className="field-hint">Not yet tested by Auteur.</p>
      )}
    </div>
  );
}

/** The whole priced catalogue, with search, filters and a details view for every model. */
export function CatalogBrowser({ models, providersWithKeys, selected, onUse, dossierNote, initialQuery = '', initialFilters = {}, initialOpen = '' }: {
  models: TaskModelOption[];
  providersWithKeys: ReadonlySet<string>;
  selected: string;
  onUse: (option: TaskModelOption) => void;
  dossierNote?: string;
  initialQuery?: string;
  initialFilters?: CatalogFilters;
  /** Model id whose details start open (tests and deep links). */
  initialOpen?: string;
}): React.ReactElement {
  const [query, setQuery] = useState(initialQuery);
  const [filters, setFilters] = useState<CatalogFilters>(initialFilters);
  const [open, setOpen] = useState(initialOpen);
  const matches = useMemo(() => filterCatalog(models, query, filters), [models, query, filters]);
  const rows = matches.slice(0, CATALOG_ROW_LIMIT);
  return (
    <div className="catalog" role="region" aria-label="All models">
      <input
        type="search"
        className="catalog-search"
        aria-label="Search all models"
        placeholder="Search models, e.g. claude, fast, long context"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      <div className="catalog-filters" role="group" aria-label="Filters">
        {CATALOG_FILTERS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className="chip"
            aria-pressed={!!filters[key]}
            onClick={() => setFilters((current) => ({ ...current, [key]: !current[key] }))}
          >
            {label}
          </button>
        ))}
      </div>
      <p className="field-hint" aria-live="polite">
        {matches.length === models.length ? `${models.length} models` : `${matches.length} of ${models.length} models`}
        {matches.length > rows.length ? ` · showing the first ${rows.length}; search to narrow the list` : ''}
      </p>
      <div className="catalog-scroll">
        <table>
          <thead>
            <tr><th>Model</th><th className="num">In / M</th><th className="num">Out / M</th><th className="num">Context</th><th aria-label="Choose" /></tr>
          </thead>
          <tbody>
            {rows.map((option) => {
              const id = modelId(option);
              const expanded = open === id;
              return (
                <React.Fragment key={id}>
                  <tr className={selected === id ? 'catalog-selected' : undefined}>
                    <td>
                      <button type="button" className="catalog-name" aria-expanded={expanded}
                              onClick={() => setOpen(expanded ? '' : id)}>
                        {displayName(option)}
                      </button>
                      {option.dossier && <span className="badge badge-dossier">Tested</span>}
                      <span className="catalog-id">{PROVIDER_NAMES[option.provider] ?? option.provider} · {option.model}</span>
                    </td>
                    <td className="num">{perMtok(option.input_per_mtok)}</td>
                    <td className="num">{perMtok(option.output_per_mtok)}</td>
                    <td className="num">{contextLabel(option.context_length)}</td>
                    <td>
                      {selected === id ? <span className="catalog-current">Selected</span>
                        : providersWithKeys.has(option.provider)
                          ? <button type="button" className="chip" onClick={() => onUse(option)}>Use</button>
                          : <span className="field-hint">Needs a key for {PROVIDER_NAMES[option.provider] ?? option.provider}</span>}
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="catalog-detail-row">
                      <td colSpan={5}><ModelDetails option={option} dossierNote={dossierNote} /></td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 && <p className="field-hint">No models match. Clear a filter or change the search.</p>}
      </div>
      <p className="field-hint">
        Prices are per million tokens. "Use" appears when you have a key for that provider; add keys in{' '}
        <Link to="/settings/provider-keys">Settings → Provider keys</Link>.
      </p>
    </div>
  );
}

/** The model field on the Tasks form. */
export function ModelPicker({ models, allModels, providersWithKeys, value, onChange, onUseOther, dossierNote, emptyMessage, featured = FEATURED_MODELS, initialBrowseOpen = false }: {
  /** Models for the chosen key's provider. */
  models: TaskModelOption[];
  /** Every priced model, for Browse all. */
  allModels: TaskModelOption[];
  providersWithKeys: ReadonlySet<string>;
  /** The chosen model id within ``models`` (``provider/model``), or ''. */
  value: string;
  onChange: (option: TaskModelOption) => void;
  /** A model from Browse all, possibly for another provider's key. */
  onUseOther: (option: TaskModelOption) => void;
  dossierNote?: string;
  emptyMessage: string;
  featured?: ReadonlyArray<FeaturedModel>;
  initialBrowseOpen?: boolean;
}): React.ReactElement {
  const [browsing, setBrowsing] = useState(initialBrowseOpen);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const groups = useMemo(() => pickerGroups(models, value, featured), [models, value, featured]);
  const chosen = models.find((option) => modelId(option) === value) ?? null;
  const chosenFeatured = chosen ? featured.find((entry) => modelId(entry) === value) : undefined;
  return (
    <div className="model-picker">
      <div className="field-head">
        <span className="field-label" id="task-model-label">Model</span>
        <HelpTip topic="the model">
          Which AI model writes the answer. Every option shows its price per million tokens (about
          750,000 words): "in" is what you send, "out" is what the model writes. "Tested by Auteur"
          marks models we have run ourselves; open one to see what it did well and less well. Browse
          all lists every model with a known price, so Auteur can keep the run under your limit.
        </HelpTip>
      </div>
      {groups.length === 0 && <p className="field-hint">{emptyMessage}</p>}
      <div role="radiogroup" aria-labelledby="task-model-label" className="model-groups">
        {groups.map((group) => (
          <div key={group.id} className={`model-group model-group-${group.id}`}>
            <p className="model-group-title">{group.title}</p>
            <div className="model-options">
              {visibleOptions(group.options, value, !!expanded[group.id]).map(({ option, why }) => {
                const id = modelId(option);
                return (
                  <label key={id} className={`model-option${id === value ? ' model-option-selected' : ''}`}>
                    <input type="radio" name="task-model" value={id} checked={id === value} onChange={() => onChange(option)} />
                    <span className="model-option-name">
                      {displayName(option)}
                      {option.dossier && group.id !== 'tested' && <span className="badge badge-dossier">Tested by Auteur</span>}
                    </span>
                    <span className="model-option-price">{perMtok(option.input_per_mtok)} / {perMtok(option.output_per_mtok)}</span>
                    {why && <span className="model-option-why">{why}</span>}
                  </label>
                );
              })}
            </div>
            {group.options.length > GROUP_VISIBLE && (
              <button type="button" className="link-button model-more" aria-expanded={!!expanded[group.id]}
                      onClick={() => setExpanded((state) => ({ ...state, [group.id]: !state[group.id] }))}>
                {expanded[group.id] ? 'Show fewer' : `Show ${group.options.length - visibleOptions(group.options, value, false).length} more`}
              </button>
            )}
          </div>
        ))}
      </div>
      {allModels.length > 0 && (
        <button type="button" className="link-button model-browse" aria-expanded={browsing} onClick={() => setBrowsing(!browsing)}>
          {browsing ? 'Hide the full list' : `Browse all ${allModels.length} models →`}
        </button>
      )}
      {browsing && (
        <CatalogBrowser
          models={allModels}
          providersWithKeys={providersWithKeys}
          selected={value}
          onUse={(option) => (models.some((m) => modelId(m) === modelId(option)) ? onChange(option) : onUseOther(option))}
          dossierNote={dossierNote}
        />
      )}
      {chosen && (
        <div className="model-choice" aria-live="polite">
          <p className="model-group-title">Your choice</p>
          <ModelDetails option={chosen} dossierNote={dossierNote} featured={chosenFeatured} />
        </div>
      )}
    </div>
  );
}
